/**
 * image-command-service.js — the parent-side seam for image actions.
 *
 * ── Scope, stated honestly ────────────────────────────────────────────────
 * Only three image actions have a writer that is reachable from outside
 * `glass-panel-image.js`: **replace**, **alt** and **link**. Crop and fit do
 * not. Crop is not a modal or a command — it is a set of aspect frames
 * (`free / square / 16-9 / 3-2 / 4-3 / circle`) written as element overrides,
 * with the derivation, the drag handles and the stage geometry all living
 * inside the panel. Lifting that out is a real refactor, and doing it here
 * would mean rewriting the panel in a phase whose contract is "no visual
 * change".
 *
 * So `editable-capabilities.js` declares image controls as
 * `[replace, link, alt]` and row 2 will show exactly those, with "All settings"
 * for the rest. That is a smaller pane than the prototype drew, and it is the
 * truthful one — a Crop button with no service behind it would be a control
 * that does nothing.
 *
 * ── One writer, not two ───────────────────────────────────────────────────
 * Alt and link go through the SAME sequence `glass-panel-image.js` uses:
 *
 *     resolveSectionByInstance(instanceId)
 *       → updateAltInHTML(section.html, editableIndex, value)   // pure transform
 *       → store.update(path, newHtml, label)
 *
 * Note `editableIndex`, not `eid` — the image transforms address the image by
 * its index within the section HTML, and the click payload carries it. Passing
 * an eid here would silently target the wrong image in a section with several.
 */

import { store } from '../../core/state.js';
import { resolveSectionByInstance } from '../../editing/section-utils.js';

/**
 * ── Why the image editor is INJECTED ──────────────────────────────────────
 * Importing `editing/image-editor.js` at module scope pulls a graph that does
 * real work on load — `core/asset-loader.js` starts fetching site-effect
 * scripts, which under node produces a wall of caught-and-logged failures. That
 * noise in a gate suite is not cosmetic: it is where a real error goes to hide.
 *
 * Same seam as `text-command-service.js` and `editing/collab-guard.js`. The
 * tests inject the REAL module (dynamically imported once), so the pure HTML
 * transforms are still exercised for real rather than against a stub — they
 * have no other coverage in the repo.
 */
const NOOP_IMAGE_EDITOR = {
  replaceImageForTarget: async () => {},
  getImageInfoFromHTML: () => null,
  updateAltInHTML: () => null,
  wrapImageInLink: () => null,
  unwrapImageLink: () => null
};

let _img = NOOP_IMAGE_EDITOR;

/**
 * Install the real image editor. `ui/shell.js` wires it at boot.
 * @param {object|null} impl — null restores the no-op default.
 */
export function setImageEditorImpl(impl) {
  _img = impl || NOOP_IMAGE_EDITOR;
}

/** Actions this service can actually perform. Mirrors editable-capabilities. */
export const IMAGE_ACTIONS = Object.freeze(['replace', 'link', 'alt']);

/**
 * Apply one of the pure HTML transforms and commit it.
 *
 * @param {object} payload — the active-editable payload (needs instanceId + editableIndex)
 * @param {(html: string, index: number) => string|null} transform
 * @param {string} label — history entry label
 * @returns {boolean}
 */
function commitTransform(payload, transform, label) {
  const instanceId = payload?.instanceId;
  const index = payload?.editableIndex;
  if (!instanceId || !Number.isInteger(index)) return false;
  const resolved = resolveSectionByInstance(instanceId);
  if (!resolved) return false;
  const nextHtml = transform(resolved.section.html, index);
  // The transforms return null/'' when they cannot find their target. Writing
  // that would blank the section.
  if (!nextHtml || nextHtml === resolved.section.html) return false;
  store.update(resolved.path, nextHtml, label);
  return true;
}

/**
 * Open the image picker for this image and swap it.
 * `replaceImageForTarget` owns the picker, the upload path and the asset URI —
 * none of that is re-implemented here.
 * @returns {Promise<void>}
 */
export async function replace(payload) {
  if (!payload?.instanceId) return;
  await _img.replaceImageForTarget(payload);
}

/**
 * Set or clear alt text.
 *
 * Callers typing into a field should bracket the run with
 * `beginAltEdit()` / `endAltEdit()` so a whole typed sentence is ONE history
 * entry — the panel does exactly this with a transaction around its `input`
 * handler, and without it every keystroke becomes its own undo step.
 *
 * @param {object} payload
 * @param {string} alt — '' clears it
 * @returns {boolean}
 */
export function setAlt(payload, alt) {
  return commitTransform(payload, (html, i) => _img.updateAltInHTML(html, i, alt), 'Edit alt text');
}

export function beginAltEdit() {
  store.beginTransaction('Edit alt text');
}

export function endAltEdit() {
  store.commitTransaction();
}

/**
 * Wrap the image in a link, or unwrap it when `href` is empty.
 * @returns {boolean}
 */
export function setLink(payload, href) {
  const target = String(href || '').trim();
  return target
    ? commitTransform(payload, (html, i) => _img.wrapImageInLink(html, i, target), 'Link image')
    : commitTransform(payload, (html, i) => _img.unwrapImageLink(html, i), 'Unlink image');
}

/**
 * Does this image have alt text? Drives the warning-tinted "Add alt text"
 * affordance — the one error-prevention control the current toolbar has
 * nowhere to put.
 * @returns {boolean|null} null when it cannot be determined
 */
export function hasAlt(payload) {
  if (!payload) return null;
  // The click snapshot is enough for the first paint, but it becomes stale as
  // soon as the user edits alt text, replaces the image, or undoes either
  // action. Prefer the canonical section HTML whenever the injected editor can
  // parse it, then fall back to the snapshot for older producers/tests.
  const resolved = resolveSectionByInstance(payload.instanceId);
  const index = payload.editableIndex;
  const current =
    resolved && Number.isInteger(index) && typeof _img.getImageInfoFromHTML === 'function'
      ? _img.getImageInfoFromHTML(resolved.section.html, index)
      : null;
  const alt = current && typeof current.alt === 'string' ? current.alt : payload.alt;
  if (typeof alt !== 'string') return null;
  return alt.trim().length > 0;
}
